// auth — Google sign-in through Firebase.
//
// The SDK is a DYNAMIC import from gstatic, so an unconfigured app never
// downloads it. That is the difference between "dormant" and "absent": there is
// no cost, no request, and nothing in the network tab.

import { firebaseConfig, googleClientId, isConfigured, signInAvailable } from './firebase-config.js';
import { bus, setState } from '../../src/core/state.js';
import { toast } from '../../src/core/util.js';

let app = null;
let auth = null;

async function ensure() {
  if (auth) return auth;
  if (!isConfigured()) return null;
  const [{ initializeApp }, authMod] = await Promise.all([
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-app.js'),
    import('https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js'),
  ]);
  app = initializeApp(firebaseConfig);
  auth = authMod.getAuth(app);
  authMod.onAuthStateChanged(auth, (user) => {
    setState({ user: user ? { uid: user.uid, name: user.displayName, email: user.email } : null });
    bus.emit('auth', user);
  });
  return auth;
}

export async function signIn() {
  const a = await ensure();
  if (!a) return null;
  const { GoogleAuthProvider, signInWithRedirect } = await import(
    'https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js');
  // Redirect, not popup: a popup is blocked in an installed PWA on iOS, and the
  // failure is silent — the user taps and nothing happens.
  const provider = new GoogleAuthProvider();
  if (googleClientId) provider.setCustomParameters({ client_id: googleClientId });
  await signInWithRedirect(a, provider);
  return null;
}

export async function signOut() {
  const a = await ensure();
  if (!a) return;
  const { signOut: fbSignOut } = await import('https://www.gstatic.com/firebasejs/10.12.2/firebase-auth.js');
  await fbSignOut(a);
  toast('Signed out');
}

/** The Firebase app instance, for addons that build on auth. Null when off. */
export const firebaseApp = () => app;
export { isConfigured };

export default {
  id: 'auth',

  async boot() {
    // GATED ON signInAvailable(), NOT isConfigured().
    //
    // The difference is a googleClientId, and it is the difference between an
    // app that can sign somebody in and one that cannot. `isConfigured()` is
    // true as soon as a Firebase apiKey exists — which the provisioning step
    // fills in long before anyone completes the OAuth client, a manual console
    // step many apps never finish.
    //
    // With the weaker gate, an app whose settings screen deliberately renders
    // NO sign-in control (see settingsRow) still downloaded the Firebase SDK
    // from gstatic.com on every launch and called initializeApp, which mints a
    // Firebase Installation ID and contacts Google. Nothing in the app could
    // then use any of it.
    //
    // That is a third-party request and a device identifier on every cold
    // start, for a feature the user cannot reach — and it makes "this app
    // collects no data" false on the App Store privacy label, which is where it
    // was found: two apps could not honestly declare DATA_NOT_COLLECTED because
    // of a Firebase call neither of them wanted.
    if (!signInAvailable()) return;   // no SDK download, no requests, no identifier
    await ensure();
  },

  settingsRow() {
    // No button at all when sign-in cannot work — a control that silently does
    // nothing is worse than its absence.
    if (!signInAvailable()) return null;
    const row = document.createElement('label');
    row.className = 'row';
    row.innerHTML = '<span>Account</span>';
    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = 'Sign in';

    let signedIn = false;
    bus.on('auth', (user) => {
      signedIn = !!user;
      btn.textContent = user ? 'Sign out' : 'Sign in';
    });
    btn.addEventListener('click', () => (signedIn ? signOut() : signIn()));

    row.appendChild(btn);
    return row;
  },
};
